'use client';

import { Card } from '@app/app/_components/Card/Card';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { Select } from '@app/app/_components/Input/Select/Select';
import { Input } from '@app/app/_components/Input/Input';
import { DiscountCode } from '@app/app/esales/travel/_sections/DiscountCode';
import { Address } from '@app/domain/model/Address';
import { ProductDataObject } from '@app/domain/model/eshop/home/ProductDataObject';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { ProductDataLeasing } from '@app/domain/model/eshop/transport/ProductDataLeasing';
import { CommonType } from '@app/domain/model/CommonType';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseHomeInsuranceResult } from '@app/service/eshop/home/actions/purchaseHomeInsurance';
import { formatAddress } from '@app/lib/address-formatter/formatAddress';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { SelectOption } from '@app/app/_components/Input/Select/SelectOption';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { Message } from '@app/app/_components/Message/Message';
import { Button } from '@app/app/_components/Button/Button';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    formState: FormState<PurchaseHomeInsuranceResult>;
    personalCode: string;
    address: Nullable<Address>;
    objects: ProductDataObject[];
    typeOfConstruction: Nullable<ProductDataPurpose>;
    discountCodeData: DiscountCodeData;
    handleDiscountCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleApplyDiscountClick: () => void;
    leasings: ProductDataLeasing[];
    defaultObjectType?: string;
    defaultConstructionType?: string;
    defaultTotalArea?: string;
    defaultConstructionYear?: string;
    handlePledgedToBankChange: (value: string) => void;
    pledgedToBank: Nullable<string>;
    selectedLeasing: Nullable<string>;
    handleSelectedLeasingChange: (value: string) => void;
}

export function ObjectSummaryEditableStep({
    formState,
    personalCode,
    address,
    objects,
    typeOfConstruction,
    discountCodeData,
    handleDiscountCode,
    handleApplyDiscountClick,
    leasings,
    defaultObjectType,
    defaultConstructionType,
    defaultTotalArea,
    defaultConstructionYear,
    handlePledgedToBankChange,
    pledgedToBank,
    selectedLeasing,
    handleSelectedLeasingChange,
}: Props) {
    const t = useTranslations();
    const locale = useLocale();
    const parameters = useAppParameters();

    const formattedAddress = useMemo(() => formatAddress(address), [address]);

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear; year >= currentYear - 60; year--) {
            years.push({ label: String(year), value: String(year) });
        }
        return years;
    }, []);

    const objectTypeOptions = useMemo(
        () =>
            objects.map(obj => ({
                label: obj.name,
                value: obj.code,
            })),
        [objects],
    );

    const constructionTypeOptions: SelectOption[] = useMemo(
        () =>
            typeOfConstruction?.purposes.map(purpose => ({
                label: purpose.name,
                value: purpose.code,
            })) ?? [],
        [typeOfConstruction],
    );

    const pledgedOptions = [
        { label: t('general.yes'), value: CommonType.Yes },
        { label: t('general.no'), value: CommonType.No },
    ];

    const leasingsOptions = useMemo(() => {
        if (!leasings || leasings.length === 0) {
            return [];
        }
        return leasings.map(leasing => ({
            label: leasing.name,
            value: leasing.code,
        }));
    }, [leasings]);

    const selectedLeasingName = useMemo(() => {
        if (!selectedLeasing) {
            return null;
        }
        const leasing = leasings.find(leasing => leasing.code === selectedLeasing);
        return leasing ? leasing.name : null;
    }, [leasings, selectedLeasing]);

    return (
        <div className="flex flex-col gap-8">
            <Card className="gap-2 p-6">
                <CollapsablePanel
                    label={formattedAddress || t('eshop.home.objectSummary.property')}
                    collapsable={false}>
                    <div className="px-6 pt-2">
                        <ReadOnlyField label={t('eshop.home.objectSummary.personalCode')}>{personalCode}</ReadOnlyField>
                    </div>
                </CollapsablePanel>

                <div className="flex flex-col gap-4">
                    <CollapsablePanel
                        label={t('eshop.home.objectSummary.objectInformation')}
                        collapsable={false}
                        buttonClassName="p-0 bg-transparent shadow-none">
                        <div className="flex flex-col gap-6 px-6 pt-2">
                            <Select
                                options={objectTypeOptions}
                                error={formState.violations?.objectType}
                                name="objectType"
                                label={t('eshop.home.objectSummary.objectType')}
                                placeholder={t('eshop.general.select')}
                                defaultValue={defaultObjectType}
                            />

                            <Select
                                options={constructionTypeOptions}
                                error={formState.violations?.constructionType}
                                name="constructionType"
                                label={t('eshop.home.objectSummary.constructionType')}
                                placeholder={t('eshop.general.select')}
                                defaultValue={defaultConstructionType || typeOfConstruction?.defaultPurpose}
                            />

                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                <Input
                                    type="number"
                                    name="totalArea"
                                    label={t('eshop.home.objectSummary.totalArea')}
                                    placeholder={t('eshop.home.objectSummary.totalAreaPlaceholder')}
                                    error={
                                        isViolationMessage(formState.violations?.totalArea) &&
                                        formState.violations?.totalArea.message === 'profile.validation.fieldRequired'
                                            ? formState.violations?.totalArea
                                            : undefined
                                    }
                                    defaultValue={defaultTotalArea}
                                    min={1}
                                    step={0.01}
                                    suffix={t('eshop.home.objectSummary.squareMeters')}
                                />

                                <Select
                                    options={yearOptions}
                                    error={formState.violations?.constructionYear}
                                    name="constructionYear"
                                    label={t('eshop.home.objectSummary.constructionYear')}
                                    placeholder={t('eshop.general.select')}
                                    defaultValue={defaultConstructionYear}
                                    searchable
                                />
                            </div>

                            {formState.violations?.totalArea &&
                                isViolationMessage(formState.violations?.totalArea) &&
                                formState.violations?.totalArea.message !== 'profile.validation.fieldRequired' && (
                                    <Message
                                        variant="warning"
                                        containerClassName="items-center"
                                        addon={
                                            <Button
                                                variant="outlined"
                                                preventDefault
                                                size="tiny"
                                                className="w-max"
                                                target="_blank"
                                                href={
                                                    locale === 'lt'
                                                        ? parameters.eshopHomeInsuranceInfoUrls.contactUs
                                                        : parameters.eshopHomeInsuranceInfoUrls.contactUsEn
                                                }
                                                rightAddon={<ArrowRightIcon className="size-4" />}>
                                                {t('general.contactUs')}
                                            </Button>
                                        }>
                                        {t(formState.violations?.totalArea.message)}
                                    </Message>
                                )}
                        </div>
                    </CollapsablePanel>
                </div>

                <div className="flex flex-col gap-4">
                    <CollapsablePanel
                        label={t('eshop.home.objectSummary.pledgedToBank')}
                        collapsable={false}
                        buttonClassName="p-0 bg-transparent shadow-none">
                        <div className="flex flex-col gap-4 px-6 pt-2">
                            <Select
                                options={pledgedOptions}
                                error={formState.violations?.pledgedToBank}
                                value={pledgedToBank ?? ''}
                                name="pledgedToBank"
                                onChange={handlePledgedToBankChange}
                                label={t('eshop.home.objectSummary.answer')}
                                placeholder={t('eshop.general.select')}
                            />
                            {pledgedToBank === CommonType.Yes && leasingsOptions.length > 0 && (
                                <Select
                                    options={leasingsOptions}
                                    label={t('eshop.home.objectSummary.leasing')}
                                    error={formState.violations?.leasing}
                                    name="leasing"
                                    value={selectedLeasing ?? ''}
                                    onChange={handleSelectedLeasingChange}
                                    placeholder={t('eshop.general.select')}
                                />
                            )}
                            <input type="hidden" name="leasingName" value={selectedLeasingName ?? ''} />
                        </div>
                    </CollapsablePanel>
                </div>
            </Card>

            <DiscountCode
                handleDiscountCode={handleDiscountCode}
                handleApplyDiscountClick={handleApplyDiscountClick}
                discountCodeData={discountCodeData}
            />
        </div>
    );
}
